import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import UpgradeModal from './UpgradeModal';
import './BookSearch.css';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

const BookSearch = () => {
  const { user, refreshUser } = useAuth();
  const [query, setQuery] = useState('');
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const navigate = useNavigate();

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    const normalizedQuery = query.toLowerCase().trim();
    
    // Check for special commands
    if (normalizedQuery === 'upgrademe') {
      setLoading(true);
      setError('');
      setStatusMessage('');
      try {
        const response = await axios.post(
          `${API_URL}/admin/upgrade-user`,
          { days: 30 }, // Upgrade for 30 days
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        setStatusMessage(`✓ ${response.data.message || 'Successfully upgraded!'}`);
        setQuery('');
        // Refresh user data to update premium status
        if (refreshUser) {
          await refreshUser();
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to upgrade. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (normalizedQuery === 'downgrademe') {
      setLoading(true);
      setError('');
      setStatusMessage('');
      try {
        const response = await axios.post(
          `${API_URL}/admin/downgrade-user`,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        setStatusMessage(`✓ ${response.data.message || 'Successfully downgraded!'}`);
        setQuery('');
        // Refresh user data to update premium status
        if (refreshUser) {
          await refreshUser();
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to downgrade. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Check if query contains "showmodal" (case-insensitive)
    // This works for all users, even if already upgraded
    if (normalizedQuery.includes('showmodal')) {
      console.log('Show modal triggered for query:', query);
      setShowUpgradeModal(true);
      setQuery(''); // Clear the search query
      return;
    }

    setLoading(true);
    setError('');
    setStatusMessage('');

    try {
      const response = await axios.get(`${API_URL}/books/search`, {
        params: { q: query }
      });
      setBooks(response.data.books || []);
    } catch (err) {
      console.error('Book search error:', err);
      console.error('Error response:', err.response);
      
      // Show more detailed error message
      const errorMessage = err.response?.data?.error || 
                          err.message || 
                          'Failed to search books. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBookClick = (bookId) => {
    navigate(`/book/${bookId}`);
  };

  const handleUpgrade = () => {
    const url = window.location.href.toLowerCase();
    const hasAISparkNotes = url.includes('aisparknotes');
    
    if (hasAISparkNotes) {
      // Redirect to Stripe checkout for AISparkNotes
      window.location.href = 'https://buy.stripe.com/00wfZi5Eo9bF3BfaeC0Ba0d';
    } else {
      // Default behavior for BookSummary
      window.location.href = 'booksummary://upgrade';
    }
  };

  return (
    <div className="container">
      <div className="search-header">
        <h1>Search Books</h1>
        <p>Find your next great read and get AI-powered summaries</p>
      </div>

      <form onSubmit={handleSearch} className="search-form">
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            // Clear status message when user types
            if (statusMessage) {
              setStatusMessage('');
            }
          }}
          placeholder="Search by title, author, or ISBN..."
          className="search-input"
        />
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Searching...' : 'Search'}
        </button>
      </form>

      {error && <div className="error">{error}</div>}
      {statusMessage && <div className="success-message">{statusMessage}</div>}

      {books.length > 0 && (
        <div className="grid">
          {books.map((book) => (
            <div key={book.id} className="book-card" onClick={() => handleBookClick(book.id)}>
              {book.imageUrl && (
                <img src={book.imageUrl} alt={book.title} />
              )}
              <div className="book-card-content">
                <h3>{book.title}</h3>
                <p className="book-authors">
                  {book.authors?.join(', ') || 'Unknown Author'}
                </p>
                {book.description && (
                  <div className="book-description">
                    {book.description
                      .replace(/<br\s*\/?>/gi, '\n') // Convert <br> tags to newlines first
                      .replace(/<\/p>/gi, '\n') // Convert </p> to newlines
                      .replace(/<p[^>]*>/gi, '') // Remove <p> tags
                      .replace(/<[^>]*>/g, '') // Remove remaining HTML tags
                      .replace(/&nbsp;/g, ' ') // Replace &nbsp; with spaces
                      .replace(/&amp;/g, '&') // Replace &amp; with &
                      .replace(/&lt;/g, '<') // Replace &lt; with <
                      .replace(/&gt;/g, '>') // Replace &gt; with >
                      .replace(/&quot;/g, '"') // Replace &quot; with "
                      .replace(/&#39;/g, "'") // Replace &#39; with '
                      .replace(/&apos;/g, "'") // Replace &apos; with '
                      .replace(/&#8217;/g, "'") // Replace &#8217; with '
                      .replace(/&#8220;/g, '"') // Replace &#8220; with "
                      .replace(/&#8221;/g, '"') // Replace &#8221; with "
                      .trim()
                      .substring(0, 150)}...
                  </div>
                )}
                {book.averageRating && (
                  <div className="book-rating">
                    ⭐ {book.averageRating.toFixed(1)} ({book.ratingsCount} ratings)
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && books.length === 0 && query && (
        <div className="no-results">
          <p>No books found. Try a different search term.</p>
        </div>
      )}

      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => {
          console.log('Closing upgrade modal');
          setShowUpgradeModal(false);
        }}
        onUpgrade={handleUpgrade}
      />
      
      {/* Debug: Show modal state */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{ position: 'fixed', bottom: 10, right: 10, background: 'rgba(0,0,0,0.7)', color: 'white', padding: '10px', fontSize: '12px', zIndex: 9999 }}>
          Modal state: {showUpgradeModal ? 'OPEN' : 'CLOSED'}
        </div>
      )}
    </div>
  );
};

export default BookSearch;
